import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";
import {
  getKeywordsByType,
  getThemaCodesByType,
  IProduct,
} from "~/integrations/firebase/collections";
import { Modal } from "~/components/modal/modal";
import { DescriptionType } from "~/api/client/products";
import { Text } from "~/components/text";
import { DescriptionTypeMultiSelect } from "./description-type-multi-select";

export type BulkGenerateDialog = "keywords" | "descriptions" | "themaCodes";

export function BulkGenerateDialogs({
  products,
  openDialog,
  onOpenDialogChange,
  generateKeywords,
  generateDescriptions,
  generateThemaCodes,
}: {
  products: IProduct[];
  openDialog: BulkGenerateDialog | null;
  onOpenDialogChange: (dialog: BulkGenerateDialog | null) => void;
  generateKeywords: (products: IProduct[]) => void;
  generateDescriptions: (
    products: IProduct[],
    descriptionTypes: DescriptionType[],
  ) => void;
  generateThemaCodes: (products: IProduct[]) => void;
}) {
  const { t } = useTranslation(["projectDetails", "general"]);
  const [selectedDescriptionTypes, setSelectedDescriptionTypes] = useState<
    DescriptionType[]
  >([]);

  const [showOverwriteDescriptionModal, setShowOverwriteDescriptionModal] =
    useState(false);
  const [showMissingDescriptionsWarning, setShowMissingDescriptionsWarning] =
    useState(false);
  const [booksWithMissingDescriptions, setBooksWithMissingDescriptions] =
    useState<IProduct[]>([]);

  const closeDialog = (open: boolean) => {
    if (!open) onOpenDialogChange(null);
  };

  const hasExistingKeywords = products.some((product) => {
    const { generated, final } = getKeywordsByType(product);
    return Boolean(generated.length || final.length);
  });

  const hasGeneratedThemaCodes = products.some((p) =>
    Boolean(getThemaCodesByType(p).generated.length),
  );

  const existingDescriptionTypes = useMemo(() => {
    const types: DescriptionType[] = [];
    products.forEach((product) => {
      const descriptions = product.descriptions || [];
      descriptions.forEach((description) => {
        if (description.textType && description.audiences?.length) {
          description.audiences.forEach((audience) => {
            if (description.textType) {
              types.push({
                textType: description.textType,
                audience,
              });
            }
          });
        }
      });
    });
    const uniqueTypes = types.reduce((unique, desc) => {
      const key = `${desc.textType}_${desc.audience}`;
      return unique.set(key, desc);
    }, new Map<string, DescriptionType>());
    const uniqueTypesArray = Array.from(uniqueTypes.values());
    return uniqueTypesArray;
  }, [products]);

  const checkForOverwritingDescriptions = useCallback(
    (types: DescriptionType[]) => {
      return types.some((type) =>
        products.some((product) => {
          const descriptions = product.descriptions || [];
          return descriptions.some(
            (desc) =>
              desc.textType === type.textType &&
              desc.audiences?.includes(type.audience) &&
              Boolean(desc.textGenerated?.length),
          );
        }),
      );
    },
    [products],
  );

  const handleGenerateKeywordsClick = useCallback(async () => {
    onOpenDialogChange(null);

    await generateKeywords(products);
  }, [generateKeywords, onOpenDialogChange, products]);

  const handleGenerateDescriptionsClick = useCallback(async () => {
    if (selectedDescriptionTypes.length === 0) return;

    const willOverwrite = checkForOverwritingDescriptions(
      selectedDescriptionTypes,
    );
    const missingDescriptionsBooks = products.filter((product) => {
      return !product.descriptions?.some((desc) =>
        selectedDescriptionTypes.some(
          (type) =>
            desc.textType === type.textType &&
            desc.audiences?.includes(type.audience),
        ),
      );
    });

    if (missingDescriptionsBooks.length > 0) {
      setBooksWithMissingDescriptions(missingDescriptionsBooks);
      onOpenDialogChange(null);
      setShowMissingDescriptionsWarning(true);
      return;
    }

    if (willOverwrite) {
      onOpenDialogChange(null);
      setShowOverwriteDescriptionModal(true);
      return;
    }

    onOpenDialogChange(null);
    await generateDescriptions(products, selectedDescriptionTypes);
  }, [
    generateDescriptions,
    onOpenDialogChange,
    products,
    selectedDescriptionTypes,
    checkForOverwritingDescriptions,
  ]);

  const handleMissingDescriptionsConfirm = useCallback(async () => {
    setShowMissingDescriptionsWarning(false);
    setBooksWithMissingDescriptions([]);
    const willOverwrite = checkForOverwritingDescriptions(
      selectedDescriptionTypes,
    );

    if (willOverwrite) {
      setShowOverwriteDescriptionModal(true);
      return;
    }

    generateDescriptions(products, selectedDescriptionTypes);
  }, [
    generateDescriptions,
    products,
    selectedDescriptionTypes,
    checkForOverwritingDescriptions,
  ]);

  const handleOverwriteConfirm = useCallback(async () => {
    setShowOverwriteDescriptionModal(false);
    generateDescriptions(products, selectedDescriptionTypes);
  }, [generateDescriptions, products, selectedDescriptionTypes]);

  const handleThemaCodesOverwriteConfirm = useCallback(async () => {
    onOpenDialogChange(null);
    generateThemaCodes(products);
  }, [generateThemaCodes, onOpenDialogChange, products]);

  return (
    <>
      <Modal
        id="generate-description"
        variant="primary"
        title={t("projectDetails:modals.generateDescriptions.title")}
        description={t(
          "projectDetails:modals.generateDescriptions.description",
        )}
        confirmLabel={t("general:buttons.generate")}
        confirmIconName="sparkles"
        cancelLabel={t("general:buttons.cancel")}
        open={openDialog === "descriptions"}
        onOpenChange={closeDialog}
        onConfirm={handleGenerateDescriptionsClick}
        disabledConfirm={selectedDescriptionTypes.length === 0}
      >
        <DescriptionTypeMultiSelect
          selectedDescriptionTypes={selectedDescriptionTypes}
          setSelectedDescriptionTypes={setSelectedDescriptionTypes}
          existingDescriptionTypes={existingDescriptionTypes}
        />
      </Modal>

      <Modal
        id="overwrite-description"
        variant="warning"
        title={t("projectDetails:modals.overwriteDescriptions.title")}
        description={t(
          "projectDetails:modals.overwriteDescriptions.description",
        )}
        confirmLabel={t(
          "projectDetails:modals.overwriteDescriptions.actions.confirm",
        )}
        confirmIconName="sparkles"
        cancelLabel={t("general:buttons.cancel")}
        open={showOverwriteDescriptionModal}
        onOpenChange={setShowOverwriteDescriptionModal}
        onConfirm={handleOverwriteConfirm}
      />

      <Modal
        id="overwrite-keywords"
        variant={hasExistingKeywords ? "warning" : "primary"}
        title={
          hasExistingKeywords
            ? t("projectDetails:modals.overwriteKeywords.title")
            : t("projectDetails:modals.generateKeywords.title")
        }
        description={
          hasExistingKeywords
            ? t("projectDetails:modals.overwriteKeywords.description")
            : t("projectDetails:modals.generateKeywords.description")
        }
        confirmLabel={
          hasExistingKeywords
            ? t("projectDetails:modals.overwriteKeywords.actions.confirm")
            : t("general:buttons.generate")
        }
        confirmIconName="sparkles"
        cancelLabel={t("general:buttons.cancel")}
        open={openDialog === "keywords"}
        onOpenChange={closeDialog}
        onConfirm={handleGenerateKeywordsClick}
      />

      <Modal
        id="missing-descriptions"
        variant="warning"
        title={t("projectDetails:modals.missingDescriptions.title")}
        description={t("projectDetails:modals.missingDescriptions.description")}
        confirmLabel={t("general:buttons.continue")}
        confirmIconName="sparkles"
        cancelLabel={t("general:buttons.cancel")}
        open={showMissingDescriptionsWarning}
        onOpenChange={(open) => {
          setShowMissingDescriptionsWarning(open);
          if (!open) {
            setBooksWithMissingDescriptions([]);
          }
        }}
        onConfirm={handleMissingDescriptionsConfirm}
      >
        <div className="mt-4 text-sm text-secondary-600">
          <Text variant="label" className="mb-2">
            {t("projectDetails:modals.missingDescriptions.affectedBooks", {
              count: booksWithMissingDescriptions.length,
            })}
          </Text>
          <div className="max-h-40 overflow-y-auto border rounded-md">
            <div className="flex flex-wrap gap-1 p-2">
              {booksWithMissingDescriptions.map((book) => (
                <span
                  key={book.isbn}
                  className="bg-secondary-100 px-2 py-0.5 rounded"
                >
                  <Text as="span" variant="labelSmall">
                    {book.isbn}
                  </Text>
                </span>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        id="overwrite-thema-codes"
        variant={hasGeneratedThemaCodes ? "warning" : "primary"}
        title={
          hasGeneratedThemaCodes
            ? t("projectDetails:modals.overwriteThemaCodes.title")
            : t("projectDetails:modals.generateThemaCodes.title")
        }
        description={
          hasGeneratedThemaCodes
            ? t("projectDetails:modals.overwriteThemaCodes.description")
            : t("projectDetails:modals.generateThemaCodes.description")
        }
        confirmLabel={
          hasGeneratedThemaCodes
            ? t("projectDetails:modals.overwriteThemaCodes.actions.confirm")
            : t("general:buttons.generate")
        }
        confirmIconName="sparkles"
        cancelLabel={t("general:buttons.cancel")}
        open={openDialog === "themaCodes"}
        onOpenChange={closeDialog}
        onConfirm={handleThemaCodesOverwriteConfirm}
      />
    </>
  );
}
